import React, { useEffect, useRef, useState } from "react";
import { QuizQueue } from "../Quiz/quizQueue.ts";
import {
    advance,
    aimFromPull,
    clampAim,
    fire,
    FORK,
    HOT_STREAK,
    Mode,
    PlateMiss,
    PlateState,
    plateAt,
    plateX,
    PLATE_HALF_WIDTH,
    BELT_SPEED,
    rockProgress,
    ROUND_MS,
    SHELF_Y,
    startGame,
} from "./plateGame.ts";
import { cardFaces } from "../cardFaces.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";

export interface PlateResult {
    mode: Mode;
    score: number;
    hits: number;
    wrongPlates: number;
    misses: PlateMiss[];
    correctFronts: string[];
}

interface PlateGameRunProps {
    queue: QuizQueue;
    mode: Mode;
    onFinish(result: PlateResult): void;
}

/** Where the pouch rests, just behind the fork, and how far it can be pulled. */
const REST = { x: FORK.x, y: FORK.y + 2 };
const MAX_PULL = 15;
const KEY_STEP = 2;
const KEY_STEP_BIG = 8;

function formatClock(ms: number): string {
    const totalSeconds = Math.ceil(ms / 1000);
    return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

/** The problem as a child would read it: × and −, not the worksheet's x and -. */
function prettyFront(front: string): string {
    return front.replace(/ x /g, " × ").replace(/ - /g, " − ");
}

/**
 * One round at the booth (ADR 0060).
 *
 * The engine is pure (`plateGame.ts`); this feeds it animation-frame deltas and
 * slingshot releases. State lives in a ref with a snapshot per frame, as in
 * DropGameRun, because `advance` draws from the queue (0053 §8).
 *
 * The booth is one SVG in the engine's own units (100 x 75), so a pointer
 * position converts to the engine's coordinates with one rect lookup and the
 * plates, the aim line and the rock all agree about where things are.
 *
 * Aiming (§1): press anywhere on the booth and drag back - the pouch moves by
 * the drag, the dotted line shows where the rock will land, and releasing
 * fires. Or focus the booth and use the arrow keys and Space. No animation
 * frames in a hidden tab means play time simply stops there.
 */
export function PlateGameRun({ queue, mode, onFinish }: PlateGameRunProps) {
    const t = useTranslation();
    const stateRef = useRef<PlateState | null>(null);
    if (stateRef.current === null) {
        stateRef.current = startGame(mode, queue, getRandomInt);
    }
    const [view, setView] = useState<PlateState>(stateRef.current);
    // The aim lives in a ref as well as state: key presses can arrive faster
    // than React re-renders, and each must step from the last one, not from
    // a stale render - nor may Space fire at an aim a render behind.
    const aimRef = useRef(50);
    const [aim, setAimState] = useState(50);
    const setAim = (x: number) => {
        aimRef.current = x;
        setAimState(x);
    };
    const [pouch, setPouch] = useState(REST);
    const [pulling, setPulling] = useState(false);
    const [aimAnnouncement, setAimAnnouncement] = useState("");
    const dragStart = useRef<{ x: number; y: number } | null>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const reported = useRef(false);

    const over = view.phase === "over";

    useEffect(() => {
        if (over) {
            return;
        }
        let frame = 0;
        let last = performance.now();
        const tick = (now: number) => {
            const next = advance(stateRef.current!, now - last, queue, getRandomInt);
            last = now;
            stateRef.current = next;
            setView(next);
            if (next.phase !== "over") {
                frame = requestAnimationFrame(tick);
            }
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [over, queue]);

    useEffect(() => {
        svgRef.current?.focus();
    }, []);

    useEffect(() => {
        if (over && !reported.current) {
            reported.current = true;
            const s = stateRef.current!;
            onFinish({
                mode: s.mode,
                score: s.score,
                hits: s.hits,
                wrongPlates: s.wrongPlates,
                misses: s.misses,
                correctFronts: s.correctFronts,
            });
        }
    }, [over, onFinish]);

    function shoot(x: number) {
        const next = fire(stateRef.current!, x);
        stateRef.current = next;
        setView(next);
    }

    /** A pointer position in booth units. */
    function toBooth(e: React.PointerEvent): { x: number; y: number } {
        const rect = svgRef.current!.getBoundingClientRect();
        return {
            x: ((e.clientX - rect.left) / rect.width) * 100,
            y: ((e.clientY - rect.top) / rect.height) * 75,
        };
    }

    function pullTo(e: React.PointerEvent) {
        const start = dragStart.current!;
        const p = toBooth(e);
        let dx = p.x - start.x;
        let dy = p.y - start.y;
        const length = Math.hypot(dx, dy);
        if (length > MAX_PULL) {
            dx = (dx / length) * MAX_PULL;
            dy = (dy / length) * MAX_PULL;
        }
        const next = { x: REST.x + dx, y: REST.y + dy };
        setPouch(next);
        const landing = aimFromPull(next);
        if (landing !== null) {
            setAim(landing);
        }
        return next;
    }

    function onPointerDown(e: React.PointerEvent<SVGSVGElement>) {
        if (over) {
            return;
        }
        e.preventDefault();
        svgRef.current?.focus();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        dragStart.current = toBooth(e);
        setPulling(true);
    }

    function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
        if (dragStart.current) {
            pullTo(e);
        }
    }

    function onPointerUp(e: React.PointerEvent<SVGSVGElement>) {
        if (!dragStart.current) {
            return;
        }
        const released = pullTo(e);
        dragStart.current = null;
        setPulling(false);
        setPouch(REST);
        const landing = aimFromPull(released);
        // Too short a pull, or not backwards, is no shot (§1).
        if (landing !== null) {
            shoot(landing);
        }
    }

    function onKeyDown(e: React.KeyboardEvent<SVGSVGElement>) {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            e.preventDefault();
            const step = (e.shiftKey ? KEY_STEP_BIG : KEY_STEP) * (e.key === "ArrowLeft" ? -1 : 1);
            const next = clampAim(aimRef.current + step);
            setAim(next);
            const target = plateAt(stateRef.current!, next);
            setAimAnnouncement(
                target ? format(t("flashCards.platesAimingAtTemplate"), { n: target.value }) : t("flashCards.platesAimingAtNothing")
            );
        } else if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            shoot(aimRef.current);
        }
    }

    const faces = cardFaces(view.problem);
    const countdownSeconds = Math.max(1, Math.ceil(view.countdownLeftMs / 1000));
    const event = view.lastEvent;

    // The rock flies from the fork to its landing point, shrinking with distance.
    const progress = rockProgress(view);
    const rock = view.rock
        ? {
              x: FORK.x + (view.rock.targetX - FORK.x) * progress,
              y: FORK.y + (SHELF_Y - FORK.y) * progress - Math.sin(progress * Math.PI) * 4,
              r: 2.6 - progress * 1.1,
          }
        : null;

    let verdict = "";
    let verdictClass = "";
    if (event?.kind === "hit") {
        verdict = format(t("flashCards.platesVerdictHitTemplate"), { n: event.points });
        verdictClass = " quizVerdict--correct";
    } else if (event?.kind === "late") {
        verdict = format(t("flashCards.platesVerdictLateTemplate"), { n: event.value });
        verdictClass = " quizVerdict--skipped";
    } else if (event?.kind === "wrong") {
        verdict = format(t("flashCards.platesVerdictWrongTemplate"), { n: event.value });
        verdictClass = " quizVerdict--wrong";
    } else if (event?.kind === "miss") {
        verdict = t("flashCards.platesVerdictMiss");
    }

    return (
        <div className="quizRun plateRun">
            <div className="quizStatus plateStatus">
                <span className="quizScore">{format(t("flashCards.scoreTemplate"), { n: view.score })}</span>
                <span className="plateClock" aria-label={t("flashCards.timeLeftAria")}>
                    {formatClock(view.phase === "countdown" ? ROUND_MS : ROUND_MS - view.playMs)}
                </span>
                {view.streak >= HOT_STREAK && <span className="plateHot">{t("flashCards.platesHotStreak")}</span>}
            </div>

            <p className="plateSign" aria-live="polite">
                {view.phase === "countdown" ? t("flashCards.getReady") : `${prettyFront(faces.front)} = ?`}
            </p>

            <div className="plateBoothWrap">
                <svg
                    ref={svgRef}
                    className={`plateBooth plateBooth--${mode}${pulling ? " plateBooth--pulling" : ""}`}
                    viewBox="0 0 100 75"
                    tabIndex={0}
                    role="application"
                    aria-label={t("flashCards.platesBoothAria")}
                    aria-describedby="plateBoothHelp"
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={() => {
                        dragStart.current = null;
                        setPulling(false);
                        setPouch(REST);
                    }}
                    onKeyDown={onKeyDown}
                >
                    {/* The awning and the back wall. */}
                    <rect x={0} y={0} width={100} height={75} className="plateWall" />
                    {Array.from({ length: 10 }, (_, i) => (
                        <path
                            key={i}
                            d={`M${i * 10} 0 H${i * 10 + 10} V7 Q${i * 10 + 5} 11 ${i * 10} 7 Z`}
                            className={i % 2 ? "plateAwning plateAwning--alt" : "plateAwning"}
                        />
                    ))}

                    {/* The shelf - a belt in hard mode, its stripes moving with the plates. */}
                    <rect x={0} y={SHELF_Y + 7} width={100} height={4} className="plateShelf" />
                    {mode === "hard" && (
                        <g className="plateBelt">
                            {Array.from({ length: 26 }, (_, i) => {
                                const x = ((i * 5 + view.playMs * BELT_SPEED) % 130) - 15;
                                return <line key={i} x1={x} y1={SHELF_Y + 7} x2={x - 2} y2={SHELF_Y + 11} />;
                            })}
                        </g>
                    )}

                    {view.plates.map((plate) =>
                        plate.broken ? null : (
                            <g key={`${plate.slot}-${plate.value}`} className="plate" transform={`translate(${plateX(mode, plate.slot, view.playMs)} ${SHELF_Y})`}>
                                <circle r={PLATE_HALF_WIDTH} className="plateRim" />
                                <circle r={PLATE_HALF_WIDTH - 1.6} className="plateFace" />
                                <text className="plateValue" textAnchor="middle" dominantBaseline="central">
                                    {view.phase === "countdown" ? "" : plate.value}
                                </text>
                            </g>
                        )
                    )}

                    {view.shatters.map((s) => (
                        <g key={s.id} transform={`translate(${s.x} ${SHELF_Y})`} className={`plateShatter${s.right ? " plateShatter--right" : ""}`}>
                            {[0, 1, 2, 3, 4, 5].map((i) => (
                                <path key={i} d="M0 0 L3 -1 L1.5 2.5 Z" className={`plateShard plateShard--${i}`} />
                            ))}
                            {s.right && event?.kind === "hit" && event.seq === s.id && (
                                <text className="platePoints" textAnchor="middle" y={-9}>
                                    +{event.points}
                                </text>
                            )}
                        </g>
                    ))}

                    {/* Where the rock will land. */}
                    {!over && view.phase === "playing" && (
                        <line x1={FORK.x} y1={FORK.y} x2={aim} y2={SHELF_Y} className={`plateAim${pulling ? " plateAim--pulling" : ""}`} />
                    )}

                    {/* The slingshot: the frame, the bands, and the pouch. */}
                    <path d={`M${FORK.x} 75 V${FORK.y + 6} M${FORK.x} ${FORK.y + 6} L${FORK.x - 4} ${FORK.y} M${FORK.x} ${FORK.y + 6} L${FORK.x + 4} ${FORK.y}`} className="plateSlingFrame" />
                    <path
                        d={`M${FORK.x - 4} ${FORK.y} L${pouch.x} ${pouch.y} L${FORK.x + 4} ${FORK.y}`}
                        className="plateSlingBand"
                    />
                    {!view.rock && <circle cx={pouch.x} cy={pouch.y} r={2.4} className="plateRock" />}
                    {rock && <circle cx={rock.x} cy={rock.y} r={rock.r} className="plateRock" />}

                    {view.phase === "countdown" && (
                        <g aria-hidden="true">
                            <rect x={0} y={0} width={100} height={75} className="plateOverlay" />
                            <text x={50} y={40} textAnchor="middle" className="plateCountdown">
                                {countdownSeconds}
                            </text>
                        </g>
                    )}
                </svg>
            </div>

            {/* Always rendered, always the same height, as on the quiz. */}
            <p className={`quizVerdict${verdictClass}`} aria-live="polite">
                {verdict}
            </p>
            <p className="plateAnnouncer" aria-live="polite">
                {aimAnnouncement}
            </p>
            <p id="plateBoothHelp" className="quizHint">
                {t("flashCards.platesHint")}
            </p>
        </div>
    );
}
